import type { ReactNode } from "react";
import cn from "classnames";

export interface ButtonGroupOption {
  label: string | ReactNode;
  value: string;
  disabled?: boolean;
}

interface ButtonGroupProps {
  options: ButtonGroupOption[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
  fullWidth?: boolean;
  mode?: 1 | 2;
}

export default function ButtonGroup({
  options,
  value,
  onChange,
  disabled = false,
  className,
  fullWidth = false,
  mode = 1,
}: ButtonGroupProps) {
  return (
    <div
      className={cn(
        "flex items-center",
        mode === 2 ? "radius-xs surface-base-fill button-secondary-normal" : "p-0.5 surface-tertiary-fill radius-xs",
        fullWidth && "w-full",
        disabled && "opacity-50 cursor-not-allowed",
        className,
      )}>
      {options.map((option, index) => {
        const isActive = value === option.value;
        const isFirst = index === 0;
        const isLast = index === options.length - 1;
        const isDisabled = disabled || option.disabled;
        return (
          <button
            key={option.value}
            type="button"
            disabled={isDisabled}
            onClick={() => !isDisabled && onChange(option.value)}
            className={cn(
              "px-3 py-1.5 flex justify-center items-center gap-2 text-label-md",
              mode === 2
                ? [
                    isFirst ? "rounded-l-lg" : "",
                    isLast ? "rounded-r-lg" : "",
                    !isFirst && !isLast ? "rounded-none" : "",
                  ]
                : "radius-2xs",
              fullWidth && "flex-1",
              isDisabled && !isActive && "opacity-50",
              isActive
                ? mode === 2
                  ? cn(
                      "primary-normal button-primary-normal text-white",
                      isFirst && !isLast && "border-r border-white/20",
                      isLast && !isFirst && "border-l border-white/20",
                      !isFirst && !isLast && "border-x border-white/20",
                    )
                  : "background-brand-fill segment-selected text-white"
                : cn("content-base-secondary text-body-regular-md", isDisabled ? "cursor-not-allowed" : "hover:bg-black/5 cursor-pointer"),
            )}>
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
